<template>
  <!-- Максимальная ширина 430px — мобильный формат по центру -->
  <div class="min-h-screen flex flex-col items-center" style="background: #EDE5D8;">
    <div :class="route.path.startsWith('/admin') ? 'w-full' : 'w-full max-w-[430px]'"
         class="flex flex-col min-h-screen relative">
      <RouterView v-slot="{ Component }">
        <keep-alive :include="['Diary']">
          <component :is="Component" class="flex-1 pb-nav" />
        </keep-alive>
      </RouterView>
      <NavBar v-if="(authStore.token || authStore.isTelegramApp) && !route.path.startsWith('/cooking/dish/') && !route.path.startsWith('/onboarding') && !route.path.startsWith('/help/install')" />
    </div>
  </div>
  <AuthModal v-if="!authStore.isTelegramApp" />
  <OfflineBanner />
  <AiConsentDialog />
  <AiFallbackSheet />
  <TokenWarning />
  <GuestExpiryWarning />
  <PaywallPopup />
  <LimitPaywallPopup />
  <!-- Первый пейвол: бесплатные распознавания или сообщения кончились (lib/freeLimits.js) -->
  <FirstPaywall />
  <!-- Реплика Зои с подсказкой (services/guide.js) — одна на приложение -->
  <GuideBubble />
  <!-- Статус после возврата с платёжной страницы. Глобально, а не на
       конкретной странице: SuccessURL и deep link ведут на разные маршруты,
       и обработчик на одном из них уже терял пользователей (12182). -->
  <Teleport to="body">
    <div v-if="paymentStatus" class="fixed inset-0 z-[130] flex items-end justify-center" @click.self="dismissPaymentStatus">
      <div class="absolute inset-0 bg-black/40" @click="dismissPaymentStatus" />
      <div class="relative w-full max-w-[430px] bg-cream rounded-t-3xl px-6 pt-6 pb-8 z-10 text-center" style="font-family: Inter, sans-serif;">
        <template v-if="paymentStatus === 'checking'">
          <div class="w-8 h-8 mx-auto mb-3 border-[3px] border-[#E5DFD5] border-t-[#41291A] rounded-full animate-spin" />
          <p class="text-17 font-bold text-ink mb-1">Оплата прошла</p>
          <p class="text-14 text-ink/70">Активирую подписку…</p>
        </template>
        <template v-else-if="paymentStatus === 'active'">
          <p class="text-30 mb-2 pay-pop">🎉</p>
          <p class="text-18 font-bold text-ink mb-1">{{ paymentRenewal ? 'Подписка продлена' : 'Подписка активна' }}</p>
          <p class="text-14 text-ink/70 mb-4">{{ planLabel }}<template v-if="subEndLabel"> · до {{ subEndLabel }}</template></p>
          <!-- Что открылось — только при первой покупке: продлившему это не новость.
               Пункты — по фактическим гейтам кода, не маркетинг. -->
          <div v-if="!paymentRenewal" class="text-left mx-auto mb-1" style="max-width: 320px;">
            <p v-for="(b, i) in premiumBenefits" :key="i" class="flex items-start gap-2.5 text-14 text-ink leading-snug mb-2.5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7A9B57" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="shrink-0 mt-0.5"><path d="M20 6L9 17l-5-5"/></svg>
              <span>{{ b }}</span>
            </p>
          </div>
          <p v-if="!paymentRenewal" class="text-12 text-ink/70 mb-1">Чек придёт на вашу почту</p>
        </template>
        <template v-else>
          <p class="text-17 font-bold text-ink mb-1">Платёж обрабатывается</p>
          <p class="text-14 text-ink/70 leading-relaxed">Банк подтверждает оплату — подписка появится в течение пары минут. Можно продолжать пользоваться приложением.</p>
        </template>
        <template v-if="paymentStatus === 'active' && !paymentRenewal">
          <button @click="goAskZoya"
                  class="mt-4 w-full py-3 rounded-xl text-sm font-semibold text-white bg-ink active:bg-[#2a1a10] transition-colors">
            Спросить Зою
          </button>
          <button @click="goDiary"
                  class="mt-2 w-full py-3 rounded-xl text-sm font-medium text-ink bg-ink/10 active:bg-ink/20 transition-colors">
            К дневнику
          </button>
        </template>
        <button v-else-if="paymentStatus !== 'checking'" @click="dismissPaymentStatus"
                class="mt-5 w-full py-3 rounded-xl text-sm font-medium text-white bg-ink active:bg-[#2a1a10] transition-colors">
          Хорошо
        </button>
      </div>
    </div>
  </Teleport>
  <GuestLimitPopup />
  <CookieBanner />
  <UpdatePopup />
  <TelegramEmailWarning />
  <ReminderHost />
</template>

<script setup>
import { onMounted, ref, computed, watch, nextTick } from 'vue'
import { RouterView, useRoute, useRouter } from 'vue-router'
import { Capacitor } from '@capacitor/core'
import { App as CapApp } from '@capacitor/app'
import { Browser } from '@capacitor/browser'
import NavBar from './components/NavBar.vue'
import AuthModal from './components/AuthModal.vue'
import AiConsentDialog from './components/AiConsentDialog.vue'
import OfflineBanner from './components/OfflineBanner.vue'
import AiFallbackSheet from './components/AiFallbackSheet.vue'
import TokenWarning from './components/TokenWarning.vue'
import PaywallPopup from './components/PaywallPopup.vue'
import LimitPaywallPopup from './components/LimitPaywallPopup.vue'
import FirstPaywall from './components/FirstPaywall.vue'
import GuestLimitPopup from './components/GuestLimitPopup.vue'
import GuestExpiryWarning from './components/GuestExpiryWarning.vue'
import CookieBanner from './components/CookieBanner.vue'
import UpdatePopup from './components/UpdatePopup.vue'
import TelegramEmailWarning from './components/TelegramEmailWarning.vue'
import GuideBubble from './components/GuideBubble.vue'
import guide from './services/guide.js'
import ReminderHost from './components/ReminderHost.vue'
import { useAuthStore } from './store/auth.js'
import api from './services/api.js'
import { track } from './analytics.js'

const authStore = useAuthStore()

// Подсказки Зои: состояние грузится при входе и при смене аккаунта
watch(() => authStore.user?.id, (id) => { guide.load(id ? authStore.user : null) }, { immediate: true })
const route = useRoute()
const router = useRouter()

// ── Возврат с платёжной страницы ─────────────────────────────────────────────
// Ловим ?payment=success на ЛЮБОМ маршруте: SuccessURL Т-Банка, deep link
// нативных сборок и IAP ведут на разные страницы. Проверка двухслойная:
// /payment/check-pending сам спрашивает статус у банка (GetState) и
// активирует заказ, не дожидаясь вебхука; затем перечитываем пользователя.
const paymentStatus = ref(null) // null | 'checking' | 'active' | 'pending'
const paymentRenewal = ref(false)
let paymentFlowRunning = false

// Что реально открывает подписка — по фактическим гейтам кода
// (checkSubscription, checkTokenLimitFreeOnly, лимиты токенов)
const premiumBenefits = [
  'Чат с Зоей и анализ дня, недели и месяца — без ограничений триала',
  'Распознавание еды по фото — без дневного лимита',
  'Лимит общения с Зоей в 10 раз выше',
]

const PLAN_LABELS = { '1_month': 'Месячная подписка', '12_months': 'Годовая подписка' }
const planLabel = computed(() =>
  PLAN_LABELS[authStore.user?.subscription_plan] || 'Подписка')
const subEndLabel = computed(() => {
  const end = authStore.user?.subscription_end
  if (!end) return null
  return new Date(end).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
})

async function runPaymentSuccessFlow() {
  if (paymentFlowRunning) return
  paymentFlowRunning = true
  paymentStatus.value = 'checking'
  // Продление или первая покупка — решает состояние ДО активации; сервер
  // подтверждает то же флагом renewal из check-pending
  paymentRenewal.value = !!authStore.user?.is_premium
  try {
    const deadline = Date.now() + 60000
    while (Date.now() < deadline) {
      try {
        const { data } = await api.post('/payment/check-pending')
        if (data?.activated) paymentRenewal.value = !!data.renewal
      } catch {}
      await authStore.checkAuthStatus()
      if (authStore.user?.is_premium) {
        paymentStatus.value = 'active'
        track('payment_welcome_shown', { renewal: paymentRenewal.value })
        return
      }
      await new Promise(r => setTimeout(r, 2500))
    }
    // Минута без подтверждения: не пугаем, банк бывает медленным.
    // Активация в итоге придёт вебхуком или следующим check-pending.
    paymentStatus.value = 'pending'
  } finally {
    paymentFlowRunning = false
  }
}

function dismissPaymentStatus() {
  if (paymentStatus.value === 'checking') return
  paymentStatus.value = null
}

// CTA к первому ценному действию: чат — флагманская premium-ценность,
// и приветствие от Зои там уже ждёт (insertPremiumWelcome на сервере)
function goAskZoya() {
  track('payment_welcome_cta', { cta: 'chat' })
  paymentStatus.value = null
  router.push('/zoya')
}
function goDiary() {
  track('payment_welcome_cta', { cta: 'diary' })
  paymentStatus.value = null
  router.push('/diary')
}

watch(() => route.query.payment, (v) => {
  if (v !== 'success') return
  // Убираем маркер из адреса, чтобы обновление страницы не запускало проверку заново
  router.replace({ path: route.path, query: { ...route.query, payment: undefined } })
  runPaymentSuccessFlow()
}, { immediate: true })
// Ключ для принудительного re-mount страницы после deep link OAuth.
// Android WebView может не перерисовать DOM после возврата из Browser.open().
const viewKey = ref(0)

onMounted(async () => {
  await authStore.checkAuthStatus()

  if (authStore.token) {
    authStore.fetchTokenBudget()

    // Вошёл через соцсеть, но почты нет — просим привязать. Без неё войти
    // заново нечем: у VK-аккаунтов по телефону email не приходит вовсе,
    // а вход через Telegram отключён.
    if ((authStore.user?.telegram_id || authStore.user?.vk_id) && !authStore.user?.email
        && !sessionStorage.getItem('tg-email-warning-dismissed')) {
      authStore.showTelegramEmailWarning = true
    }
  } else if (!authStore.isTelegramApp && !route.path.startsWith('/legal/') && !route.path.startsWith('/help/') && !route.path.startsWith('/install') && !route.path.startsWith('/onboarding') && route.path !== '/login') {
    // Пользователь не авторизован — показать попап авторизации (кроме юр., help и онбординга)
    authStore.openAuthPopup()
  }

  // Capacitor: слушаем deep link для OAuth и оплаты
  if (Capacitor.isNativePlatform()) {
    let authDeepLinkHandled = false

    async function handleDeepLink(url) {
      // OAuth callback: zoya://auth?token=...
      if (url.startsWith('zoya://auth')) {
        if (authDeepLinkHandled) return
        authDeepLinkHandled = true
        try { await Browser.close() } catch {}
        try {
          const params = new URL(url).searchParams
          const token = params.get('token')
          const urlError = params.get('error')
          if (token) {
            // Этим токеном уже вошли — ссылка пришла повторно. На iOS
            // getLaunchUrl() отдаёт не URL запуска, а последний открытый URL
            // и никогда его не очищает: после window.location.replace() ниже
            // приложение стартует заново, снова получает тот же
            // zoya://auth?token=…, снова перезагружается — бесконечное
            // мигание экрана. На Android то же самое при холодном старте
            // по deep link (intent запуска не меняется до перезапуска Activity).
            if (authStore.token === token) return
            await authStore.loginWithToken(token)
            // Android WebView не перерисовывает DOM после Browser.close():
            // viewKey++ пересоздаёт компонент в Virtual DOM, но WebView
            // не перерисовывает, пока пользователь не тапнет экран.
            // Полная навигация (window.location) гарантирует перерисовку.
            if (!authStore.error && authStore.user) {
              const dest = authStore.user.onboarding_done ? '/diary' : '/onboarding'
              window.location.replace(dest)
              return
            }
          } else if (urlError === 'failed') {
            authStore.error = 'Ошибка авторизации. Попробуйте ещё раз'
          }
        } catch (e) {
          authStore.error = `Deep link ошибка: ${e.message}`
        }
      }

      // Payment callback: zoya://payment?status=success|error
      if (url.startsWith('zoya://payment')) {
        try { await Browser.close() } catch {}
        try {
          const params = new URL(url).searchParams
          const status = params.get('status')
          if (status === 'success') {
            router.push('/profile/settings?payment=success')
          } else {
            router.push('/subscription?payment=error')
          }
        } catch (e) {
          console.error('Payment deep link error:', e)
          router.push('/subscription')
        }
      }
    }

    CapApp.addListener('appUrlOpen', ({ url }) => handleDeepLink(url))

    // Cold start: если приложение было убито ОС и перезапущено по deep link,
    // событие appUrlOpen может не сработать — проверяем launch URL
    CapApp.getLaunchUrl().then((result) => {
      if (result?.url) handleDeepLink(result.url)
    })
  }

  // Telegram Mini App: настраиваем BackButton
  const tg = window.Telegram?.WebApp
  if (tg?.initData) {
    // BackButton: показывать/скрывать в зависимости от маршрута
    const updateBackButton = () => {
      if (route.path === '/diary' || route.path === '/') {
        tg.BackButton.hide()
      } else {
        tg.BackButton.show()
      }
    }
    tg.BackButton.onClick(() => router.back())
    watch(() => route.path, updateBackButton, { immediate: true })
  }
})
</script>

<style scoped>
.pay-pop {
  display: inline-block;
  animation: pay-pop .45s cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes pay-pop {
  from { transform: scale(0.4); opacity: 0; }
  to   { transform: scale(1); opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .pay-pop { animation: none; }
}
</style>
